/* eslint-disable @typescript-eslint/no-inferrable-types */
import { apiUserAccountService } from '@api/services/account.apiService';
import { IApiUserIpInfo } from '@api/services/account.apiService.interface';
import { CountryInfo } from '@components-shared/countryDetection/countryDetector';
import { cookieService } from '@utils/library/cookie.service';
import {
  IGeneralSettings,
  IOnboardingConfiguration,
  IUser,
  IUserDocument,
  IUserIpInfo,
  IUserProfile,
} from 'global.interfaces';
import { makeAutoObservable, runInAction } from 'mobx';
import { setUserId } from 'utils/userInfoStore';
import type { MobxRootStore } from './mobx.store';

const FOUR_HOURS_IN_SECONDS = 14400;

export class AccountStore {
  mobxStore: MobxRootStore;

  private _generalSettings: IGeneralSettings | null = null;
  private _generalSettingsLoading: boolean = true;
  private _userInfo: IUser | null | Record<string, never> = null;
  private _userInfoLoading: boolean = false;
  private _onboardingConfigurationLoading: boolean = true;
  private _onboardingConfiguration: IOnboardingConfiguration | null = null;
  private _userProfile: IUserProfile | Record<string, never | { profile: Record<string, never | string> }> = {};
  private _userProfileLoading: boolean = true;
  private _userDocuments: IUserDocument[] = [];
  private _userDocumentsLoading: boolean = true;
  private _userDocumentsProgress: number = 0;
  private _userIpInfo: IUserIpInfo | null = null;

  constructor(mobxStore: MobxRootStore) {
    this.mobxStore = mobxStore;
    makeAutoObservable(this);
  }

  fetchGeneralSettings = () => {
    if (this._generalSettings) {
      console.error('generalSettings are loaded');
    }

    runInAction(() => {
      this._generalSettingsLoading = true;
      this._generalSettings = window.generalSettings as IGeneralSettings;
      this._generalSettingsLoading = false;
    });
  };

  fetchUserInfo = async (options: { showLoader?: boolean; isInitialRequest?: boolean } = {}) => {
    if (options?.showLoader) {
      this._userInfoLoading = true;
    }

    try {
      const response = await apiUserAccountService.getUserSettings({ isInitialAuth: options.isInitialRequest });

      runInAction(() => {
        this._userInfo = response.data;
        this._userInfoLoading = false;

        setUserId(response.data?.id);
      });
    } catch (error) {
      runInAction(() => {
        this._userInfoLoading = false;
      });
    }
  };

  fetchUserProfile = async () => {
    this._userProfileLoading = true;
    try {
      const response = await apiUserAccountService.getUserProfile();

      runInAction(() => {
        this._userProfile = response.data;
        this._userProfileLoading = false;
      });
    } catch (error) {
      runInAction(() => {
        this._userProfile = {};
        this._userProfileLoading = false;
      });
    }
  };

  fetchUserDocuments = async () => {
    this._userDocumentsLoading = true;
    try {
      const response = await apiUserAccountService.getUserDocuments({ skip: 0, take: 100 });

      runInAction(() => {
        this._userDocuments = response.data.records ?? [];
        this._userDocumentsLoading = false;
      });
    } catch (error) {
      this._userDocuments = [];
      this._userDocumentsLoading = false;
    }
  };

  fetchUserDocumentsSilently = async () => {
    try {
      const response = await apiUserAccountService.getUserDocuments({ skip: 0, take: 100 });

      this._userDocuments = response.data.records ?? [];
    } catch (error) {
      this._userDocuments = [];
    }
  };

  fetchUserIpInfo = async () => {
    try {
      const cookie = cookieService.getCookie('__ipInfo');
      let responseData: IApiUserIpInfo | null = cookie ? JSON.parse(cookie) : null;

      if (!responseData) {
        const { data } = await apiUserAccountService.getUserIpInfo();

        responseData = {
          country_code: data.country_code,
          country_name: data.country_name,
          location: { calling_code: data.location?.calling_code },
        };

        cookieService.setCookie('__ipInfo', JSON.stringify(responseData), undefined, FOUR_HOURS_IN_SECONDS);
      }

      runInAction(() => {
        this._userIpInfo = responseData && Object.keys(responseData).length > 0 ? responseData : null;
      });
    } catch (error) {
      runInAction(() => {
        this._userIpInfo = null;
      });
      console.error(error);
    }
  };

  setUserDocumentsProgress = (value: number) => {
    this._userDocumentsProgress = value;
  };

  fetchOnboardingConfiguration = async () => {
    this._onboardingConfigurationLoading = true;

    /** @description Legacy code. Migrated from Redux. */
    this._onboardingConfiguration = {} as IOnboardingConfiguration;

    try {
      const response = await apiUserAccountService.getOnboardingConfiguration();

      runInAction(() => {
        this._onboardingConfiguration = response.data;
        this._onboardingConfigurationLoading = false;
      });
    } catch (err) {
      runInAction(() => {
        this._onboardingConfigurationLoading = false;
      });
    }
  };

  initStoryBookGeneralSettings = () => {
    this._generalSettings = { utcOffsetInHours: 0 } as IGeneralSettings;
  };

  get generalSettings(): IGeneralSettings {
    if (!this._generalSettings) {
      throw new Error('generalSettings is missing');
    }
    return this._generalSettings as IGeneralSettings;
  }

  get generalSettingsLoading() {
    return this._generalSettingsLoading;
  }

  get initialUserInfo() {
    return this._userInfo;
  }

  get userInfo() {
    if (!this._userInfo) {
      throw new Error('userInfo is missing');
    }
    return this._userInfo as IUser;
  }

  get userInfoLoading() {
    return this._userInfoLoading;
  }

  get userProfile(): IUserProfile {
    const getIsUserProfile = (profile: typeof this._userProfile): profile is IUserProfile => {
      return !!profile?.profile;
    };

    if (getIsUserProfile(this._userProfile)) {
      return this._userProfile;
    } else {
      /**
       * @description userProfile is empty object in this case
       * legacy code
       */

      return this._userProfile as unknown as IUserProfile;
    }
  }

  get userProfileLoading() {
    return this._userProfileLoading;
  }

  get onboardingConfiguration() {
    return this._onboardingConfiguration;
  }

  get onboardingConfigurationLoading() {
    return this._onboardingConfigurationLoading;
  }

  get userDocuments() {
    return this._userDocuments;
  }

  get userDocumentsLoading() {
    return this._userDocumentsLoading;
  }

  get userDocumentsProgress() {
    return this._userDocumentsProgress;
  }

  get telemetryIKey() {
    return this._generalSettings?.iKey;
  }

  get utcOffsetInHoursFormattedString() {
    const { utcOffsetInHours } = this.generalSettings;

    const sign = utcOffsetInHours > 0 ? '+' : '';

    return `(GMT${sign}${utcOffsetInHours})`;
  }

  get utcOffsetInHours() {
    return this._generalSettings ? this._generalSettings.utcOffsetInHours : 0;
  }

  get brokerParams() {
    if (!this._generalSettings) {
      console.error('generalSettings is missing');
      return null;
    }

    return this._generalSettings.brokerParams;
  }

  get userCountryInfo(): CountryInfo | null {
    if (!this._userIpInfo) {
      return null;
    }
    return {
      countryCode: this._userIpInfo.country_code,
      countryName: this._userIpInfo.country_name,
      callingCode: this._userIpInfo.location?.calling_code,
    };
  }
}
